import { useState, useEffect } from 'react';
import { useSwimming } from '../../context/SwimmingContext';

export const useCompetitionStates = () => {
  const { swimmers, isRunning, setIsRunning } = useSwimming();

  const [currentTime, setCurrentTime] = useState(() => {
    const saved = localStorage.getItem('currentTime');
    return saved ? parseInt(saved) : 0;
  });

  const [totalTime, setTotalTime] = useState(() => {
    const saved = localStorage.getItem('totalTime');
    return saved ? parseInt(saved) : 0;
  });

  const [startTime, setStartTime] = useState(() => {
    const saved = localStorage.getItem('startTime');
    return saved ? parseInt(saved) : null;
  });

  const [totalStartTime, setTotalStartTime] = useState(() => {
    const saved = localStorage.getItem('totalStartTime');
    return saved ? parseInt(saved) : null;
  });

  const [isPaused, setIsPaused] = useState(() => {
    const saved = localStorage.getItem('isPaused');
    return saved ? JSON.parse(saved) : false;
  });

  const [pausedTime, setPausedTime] = useState(() => {
    const saved = localStorage.getItem('pausedTime');
    return saved ? parseInt(saved) : 0;
  });

  const [currentSwimmerId, setCurrentSwimmerId] = useState(() => {
    const saved = localStorage.getItem('currentSwimmerId');
    return saved || (swimmers.length > 0 ? swimmers[0] : '');
  });

  const [swimmersData, setSwimmersData] = useState(() => {
    const saved = localStorage.getItem('swimmersData');
    if (saved) {
      const parsedData = JSON.parse(saved);
      // Garantir que todos os nadadores tenham os dados necessários
      const updatedData = { ...parsedData };
      swimmers.forEach(swimmer => {
        if (!updatedData[swimmer]) {
          updatedData[swimmer] = {
            currentLapTime: 0,
            laps: [],
            totalDistance: 0,
            average: 0
          };
        }
      });
      return updatedData;
    }
    const initialData = {};
    swimmers.forEach(swimmer => {
      initialData[swimmer] = {
        currentLapTime: 0,
        laps: [],
        totalDistance: 0,
        average: 0
      };
    });
    return initialData;
  });

  const [swimmersList, setSwimmersList] = useState(() => {
    const saved = localStorage.getItem('swimmersList');
    if (saved) {
      const parsedList = JSON.parse(saved);
      return parsedList.length > 0 ? parsedList : swimmers;
    }
    return swimmers;
  });

  const [newSwimmer, setNewSwimmer] = useState('');

  const [hasFirstLap, setHasFirstLap] = useState(() => {
    const saved = localStorage.getItem('hasFirstLap');
    return saved ? JSON.parse(saved) : false;
  });

  const [deleteConfirmation, setDeleteConfirmation] = useState({ open: false, swimmer: null });
  const [stopConfirmation, setStopConfirmation] = useState(false);
  const [isCompetitionEnded, setIsCompetitionEnded] = useState(() => {
    const saved = localStorage.getItem('isCompetitionEnded');
    return saved ? JSON.parse(saved) : false;
  });

  // Efeito para sincronizar o estado da competição
  useEffect(() => {
    const savedState = localStorage.getItem('competitionState');
    if (savedState) {
      const state = JSON.parse(savedState);
      setCurrentTime(state.currentTime || 0);
      setTotalTime(state.totalTime || 0);
      setStartTime(state.startTime);
      setTotalStartTime(state.totalStartTime);
      setIsPaused(state.isPaused || false);
      setPausedTime(state.pausedTime || 0);
      setHasFirstLap(state.hasFirstLap || false);
      setIsCompetitionEnded(state.isCompetitionEnded || false);
    }
  }, []);

  // Efeito para salvar o estado da competição
  useEffect(() => {
    const state = {
      currentTime,
      totalTime,
      startTime,
      totalStartTime,
      isPaused,
      pausedTime,
      hasFirstLap,
      isCompetitionEnded
    };
    localStorage.setItem('competitionState', JSON.stringify(state));
  }, [
    currentTime,
    totalTime,
    startTime,
    totalStartTime,
    isPaused,
    pausedTime,
    hasFirstLap,
    isCompetitionEnded
  ]);

  // Efeitos individuais para persistência
  useEffect(() => {
    localStorage.setItem('currentTime', currentTime.toString());
  }, [currentTime]);

  useEffect(() => {
    localStorage.setItem('totalTime', totalTime.toString());
  }, [totalTime]);

  useEffect(() => {
    if (startTime) {
      localStorage.setItem('startTime', startTime.toString());
    }
  }, [startTime]);

  useEffect(() => {
    if (totalStartTime) {
      localStorage.setItem('totalStartTime', totalStartTime.toString());
    }
  }, [totalStartTime]);

  useEffect(() => {
    localStorage.setItem('isPaused', JSON.stringify(isPaused));
  }, [isPaused]);

  useEffect(() => {
    localStorage.setItem('pausedTime', pausedTime.toString());
  }, [pausedTime]);

  useEffect(() => {
    if (currentSwimmerId) {
      localStorage.setItem('currentSwimmerId', currentSwimmerId);
    }
  }, [currentSwimmerId]);

  useEffect(() => {
    localStorage.setItem('swimmersData', JSON.stringify(swimmersData));
  }, [swimmersData]);

  useEffect(() => {
    localStorage.setItem('swimmersList', JSON.stringify(swimmersList));
  }, [swimmersList]);

  useEffect(() => {
    localStorage.setItem('hasFirstLap', JSON.stringify(hasFirstLap));
  }, [hasFirstLap]);

  useEffect(() => {
    localStorage.setItem('isCompetitionEnded', JSON.stringify(isCompetitionEnded));
  }, [isCompetitionEnded]);

  // Efeito para inicialização dos dados dos nadadores
  useEffect(() => {
    if (swimmers && swimmers.length > 0) {
      const savedSwimmersList = localStorage.getItem('swimmersList');
      if (!savedSwimmersList) {
        setSwimmersList(swimmers);
        setCurrentSwimmerId(swimmers[0]);
        
        const initialData = {};
        swimmers.forEach(swimmer => {
          initialData[swimmer] = {
            currentLapTime: 0,
            laps: [],
            totalDistance: 0,
            average: 0
          };
        });
        setSwimmersData(initialData);
      }
    }
  }, [swimmers]);

  return {
    currentTime,
    setCurrentTime,
    totalTime,
    setTotalTime,
    startTime,
    setStartTime,
    totalStartTime,
    setTotalStartTime,
    isRunning,
    setIsRunning,
    isPaused,
    setIsPaused,
    pausedTime,
    setPausedTime,
    currentSwimmerId,
    setCurrentSwimmerId,
    swimmersData,
    setSwimmersData,
    swimmersList,
    setSwimmersList,
    newSwimmer,
    setNewSwimmer,
    hasFirstLap,
    setHasFirstLap,
    deleteConfirmation,
    setDeleteConfirmation,
    stopConfirmation,
    setStopConfirmation,
    isCompetitionEnded,
    setIsCompetitionEnded
  };
};